import React, { useCallback, useContext, useEffect, useState } from 'react';
import { RiUserAddLine } from "react-icons/ri";
import GenericTable from '../components/GenericTable';
import ModalForm from '../components/ModalForm';
import EmployeeDetails from '../components/modals/EmployeeDetails';
import { employeeService } from '../services/employeeService';
import { useDataSync } from '../hooks/useDataSync';
import { transformEmployeeToTable, transformEmployeeToAPI } from '../utils/dataTransforms';
import GenericFilters from '../components/GenericFilters';

import { notifyError, notifySuccess, notifyWarning } from '../utils/notifications';
import { UserContext } from '../utils/UserContext';

const Personal = () => {
    const { selectedWorkshop } = useContext(UserContext);
    const { data, setData } = useDataSync([]);
    const [isLoading, setIsLoading] = useState(false);
    const [filteredData, setFilteredData] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [workshopID, setWorkshopID] = useState(null);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [employeeIdToDelete, setEmployeeIdToDelete] = useState(null);

    useEffect(() => {
        if (selectedWorkshop) {
            setWorkshopID(selectedWorkshop._id);
        } else {
            notifyWarning("No se ha seleccionado un taller.");
        }
    }, [selectedWorkshop]);

    // Función para obtener los empleados desde la API
    const fetchEmployees = useCallback(async () => {
        if (!workshopID) return;
        setIsLoading(true);

        try {
            const employeesData = await employeeService.getEmployees(workshopID);
            const mapped = employeesData.map(transformEmployeeToTable);
            setData(mapped);
            setFilteredData(mapped);
        } catch (error) {
            console.error('Error al obtener empleados:', error);
            notifyError('No se pudieron cargar los empleados.');
        } finally {
            setIsLoading(false);
        }
    }, [workshopID, setData]);

    useEffect(() => {
        fetchEmployees();
    }, [fetchEmployees]);

    const columns = React.useMemo(() => [
        { Header: 'Nombre', accessor: 'employee_name' },
        { Header: 'Rol', accessor: 'role' },
        { Header: 'Correo Electrónico', accessor: 'email' },
        { Header: 'Teléfono', accessor: 'phone' },
        { Header: 'NSS', accessor: 'nss' },
    ], []);

    const handleViewDetails = (rowData) => {
        setSelectedEmployee(rowData);
        setIsDetailModalOpen(true);
    }

    const actionHandlers = {
        edit: handleViewDetails,
        delete: (employee) => setEmployeeIdToDelete(employee.employee_id),
    };

    const handleDeleteEmployee = async () => {
        if (!employeeIdToDelete) return;

        try {
            await employeeService.deleteEmployee(employeeIdToDelete);
            setData(prevData => prevData.filter(employee => employee.employee_id !== employeeIdToDelete));
            setFilteredData(prevData => prevData.filter(employee => employee.employee_id !== employeeIdToDelete));
            notifySuccess('Empleado eliminado correctamente.');
            setEmployeeIdToDelete(null);
        } catch (error) {
            console.error('Error al eliminar empleado:', error);
            notifyError('No se pudo eliminar el empleado.');
        }
    };

    const handleCancelDelete = () => {
        setEmployeeIdToDelete(null);
    };

    const handleAddPersonal = async (formData) => {
        const { employee_name, email, nss, phone, role } = formData;

        if (!employee_name || !email || !nss || !phone || !role) {
            notifyWarning('Todos los campos obligatorios deben ser llenados.');
            return;
        }

        try {
            const payload = transformEmployeeToAPI(formData);
            await employeeService.createEmployee(workshopID, payload);

            // Refrescar la lista desde la API
            await fetchEmployees();
            setIsModalOpen(false);
            notifySuccess('Empleado agregado correctamente.');
        } catch (error) {
            console.error('Error al agregar empleado:', error);
            notifyError('No se pudo agregar el empleado.');
        }
    };

    const handleFilterChange = (filters) => {
        const { search } = filters;
        if (!search) {
            setFilteredData(data);
            return;
        }
        const searchLower = search.toLowerCase();
        const filtered = data.filter(emp =>
            emp.employee_name.toLowerCase().includes(searchLower) ||
            emp.email.toLowerCase().includes(searchLower) ||
            emp.phone.toLowerCase().includes(searchLower)
        );
        setFilteredData(filtered);
    };

    const handleClearFilters = () => {
        setFilteredData(data);
    };

    return (
        <div className='category-container'>
            <div className="category-header">
                <h1 className="category-title">Personal</h1>
                <button
                    className="add-btn add-btn-icon"
                    onClick={() => setIsModalOpen(true)}
                >
                    <RiUserAddLine aria-hidden="true" />
                    <span>Nuevo empleado</span>
                </button>
            </div>
            <GenericFilters
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                searchPlaceholder="Buscar por nombre, email, teléfono..."
                filterLabel="Buscar Personal"
            />
            <GenericTable
                columns={columns}
                data={filteredData}
                isLoading={isLoading}
                actionList={['edit', 'delete']}
                actionHandlers={actionHandlers}
            />
            <ModalForm
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSubmit={handleAddPersonal}
                title="Añadir Empleado"
                fields={[
                    {
                        name: 'role',
                        label: 'Rol',
                        type: 'select',
                        options: [
                            { value: 'admin', label: 'Admin' },
                            { value: 'technician', label: 'Technician' }
                        ],
                        required: true
                    },
                    { name: 'employee_name', label: 'Nombre Completo', placeholder: 'Ej. Juan Perez', required: true },
                    { name: 'email', label: 'Correo Electrónico', placeholder: 'Ej. usuario@correo.com', required: true, type: 'email' },
                    { name: 'phone', label: 'Teléfono', placeholder: 'Ej. 5512345678', required: true, type: 'tel' },
                    { name: 'nss', label: 'NSS', placeholder: 'Ej. 123456789', required: true },
                    { name: 'curp', label: 'CURP', placeholder: 'Ej. ABCD123456HJKLMN09', required: false },
                    { name: 'rfc', label: 'RFC', placeholder: 'Ej. ABCD123456ABC', required: false },
                    { name: 'address', label: 'Dirección', placeholder: 'Ej. Calle 123, Col. Centro', required: false }
                ]}
            />
            {isDetailModalOpen && selectedEmployee && (
                <div className="modal-overlay">
                    <div className="modal-container modal-large">
                        <button className="modal-close-btn" onClick={() => setIsDetailModalOpen(false)}>
                            ×
                        </button>
                        <div className="modal-header">
                            <h2>Detalles del Empleado</h2>
                        </div>
                        <div className="modal-body">
                            <EmployeeDetails
                                employeeData={selectedEmployee}
                                workshopID={workshopID}
                                onClose={() => setIsDetailModalOpen(false)}
                            />
                        </div>
                    </div>
                </div>
            )}
            {employeeIdToDelete && (
                <ModalForm
                    isOpen={true}
                    onClose={handleCancelDelete}
                    title="Confirmar Eliminación"
                    size="small"
                    footer={
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <button className="cancel-btn" onClick={handleCancelDelete}>
                                Cancelar
                            </button>
                            <button
                                className="delete-btn"
                                style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}
                                onClick={handleDeleteEmployee}
                            >
                                Eliminar
                            </button>
                        </div>
                    }
                >
                    <p>¿Estás realmente seguro de que deseas eliminar este empleado? Esta acción no se puede deshacer.</p>
                </ModalForm>
            )}
        </div>
    );
};
export default Personal;
